import React, { memo, useCallback, useEffect, useState } from 'react';
import cookies from 'js-cookie';
import { useDispatch } from 'react-redux';

import { BasicLayout, cookieNames } from '@monorepo/common';

import { useAppSelector } from '../../../store/hooks';
import { useClearDataQuery } from '../../../services/servicePostAJob';
import { dropStepper } from '../../../features/sliceStepper';
import SecureCheckoutRow from './SecureCheckoutRow';

// Unified ecosystem copy — one message for every site variant.
const BAND_COPY = 'You’re posting across the entire BevNET CPG Media ecosystem (including BevNET, Brewbound, and Nosh), reaching thousands of verified beverage and food professionals every month.';

/**
 * ListingHeaderBand — full-width band above the stepper (canvas 3a). Replaces the
 * old left DescriptionJobs panel. Left: display title + per-site sub. Right (only when
 * a draft cookie exists — gated by the same `stepperOptions.isShowDropButton` redux flag
 * the old drop button used): "Draft saved" chip + "Clear this listing and start over".
 * The drop() machinery is ported VERBATIM from the former DescriptionJobs.tsx.
 */
const ListingHeaderBand = () => {
  const dispatch = useDispatch();
  // The guid to clear, captured when the user confirms — while the cookie is still
  // there. Doubles as the "clear in flight" flag the boolean used to be.
  const [clearGuid, setClearGuid] = useState<string | null>(null);
  const { isShowDopButton } = useAppSelector(({ stepperOptions }) => ({
    isShowDopButton: stepperOptions.isShowDropButton
  }));
  const { isLoading, isSuccess } = useClearDataQuery({ guid: clearGuid as string }, {
    skip: !clearGuid
  });

  const drop = useCallback(() => {
    // eslint-disable-next-line no-alert
    if (window.confirm('Are you sure that you want to start over? You will not longer be able to edit the current listing.')) {
      const guid = cookies.get(cookieNames.tempJob);
      if (guid) {
        setClearGuid(guid);
      }
    }
  }, []);

  // Only drop the local draft once the server confirms the bucket is gone.
  useEffect(() => {
    if (isSuccess && clearGuid) {
      setClearGuid(null);
      cookies.remove(cookieNames.tempJob);
      dispatch(dropStepper({
        isDrop: true
      }));

      const timeout = setTimeout(() => {
        dispatch(dropStepper({
          isDrop: false
        }));
        clearTimeout(timeout);
      }, 3000);
    }
  }, [isSuccess, clearGuid]);

  return (
    <BasicLayout isLoading={isLoading}>
      <div className="cpg-band">
        <div className="cpg-band__left">
          <span className="cpg-band__eyebrow">Every listing runs for 60 days</span>
          <h1 className="cpg-band__title">
            Post a Job Listing
          </h1>
          <p className="cpg-band__sub">{BAND_COPY}</p>
          <SecureCheckoutRow />
        </div>

        {isShowDopButton && (
          <div className="cpg-band__right">
            <span className="cpg-band__chip">Draft saved</span>
            <span
              className="cpg-band__divider"
              aria-hidden="true"
            />
            <button
              type="button"
              className="cpg-band__clear"
              onClick={drop}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M3.5 12a8.5 8.5 0 1 0 2.4-5.9"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M3 3.5V8h4.5"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Start over
            </button>
          </div>
        )}
      </div>
    </BasicLayout>
  );
};

export default memo(ListingHeaderBand);
